import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  MAX_MAX_QUESTIONS,
  MIN_MAX_QUESTIONS,
} from "@redotech/redo-model/agents/returns-agents/util/return-reason-conversation-util";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { AiReturnReasonsState } from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo } from "react";
import { AdditionalAiInfo } from "./multiple-choice";

export const AiReturnReasonsDetails = memo(function AiReturnReasonsDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<AiReturnReasonsState>) {
  const user = useContext(UserContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const isRedoUser =
    (user?.email && /@getredo\.com/.test(user?.email)) || false;

  const automationsLink = useMemo(
    () => `${window.location.href.split("/settings")[0]}/settings/automations`,
    [],
  );

  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });

  const nextWrongProductStepChange = useHandler((nextWrongProduct?: StepId) => {
    setState((state) => ({ ...state, nextWrongProduct }));
  });

  const nextDamagedStepChange = useHandler((nextDamaged?: StepId) => {
    setState((state) => ({ ...state, nextDamaged }));
  });

  const nextExchangeStepChange = useHandler((nextExchange?: StepId) => {
    setState((state) => ({ ...state, nextExchange }));
  });

  const handleAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) => ({ ...state, additionalAiInfo })),
  );

  const handleMaxQuestionsChange = useHandler(
    (maxQuestions: number | undefined) => {
      setState((state) => ({ ...state, maxQuestions }));
    },
  );

  const handleGuidanceChange = useHandler(
    (merchantGuidance: string | undefined) => {
      setState((state) => ({
        ...state,
        merchantGuidance: merchantGuidance || undefined,
      }));
    },
  );

  const handleCustomQuestionChange = useHandler(
    (customQuestion: string | undefined) => {
      // Convert empty string to undefined, but don't trim while typing
      // (trimming on every keystroke prevents users from typing spaces)
      setState((state) => ({
        ...state,
        customQuestion: customQuestion || undefined,
      }));
    },
  );

  const handleCustomExplanationSubtextChange = useHandler(
    (customExplanationSubtext: string | undefined) => {
      // Convert empty string to undefined, but don't trim while typing
      // (trimming on every keystroke prevents users from typing spaces)
      setState((state) => ({
        ...state,
        customExplanationSubtext: customExplanationSubtext || undefined,
      }));
    },
  );

  function getMaxQuestionsValidationErrors(maxQuestions: number | undefined) {
    if (maxQuestions === undefined) {
      return [];
    }
    if (maxQuestions < MIN_MAX_QUESTIONS) {
      return [`Must be greater than ${MIN_MAX_QUESTIONS - 1}`];
    }
    if (maxQuestions > MAX_MAX_QUESTIONS) {
      return [`Must be less than or equal to ${MAX_MAX_QUESTIONS}`];
    }
    return [];
  }

  return (
    <>
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Default next step
          </Text>
          {stepSelect({
            value: state.next,
            valueChange: nextStepChange,
            handleIndex: 0,
          })}
          {!state.next && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              Required
            </Text>
          )}
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            If wrong product, then
          </Text>
          {stepSelect({
            value: state.nextWrongProduct,
            valueChange: nextWrongProductStepChange,
            handleIndex: 1,
          })}
          <Text fontSize="xs" textColor="tertiary">
            The step to go to if AI detects wrong product as the reason
          </Text>
          {!state.nextWrongProduct && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              Required
            </Text>
          )}
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            If damaged product, then
          </Text>

          {stepSelect({
            value: state.nextDamaged,
            valueChange: nextDamagedStepChange,
            handleIndex: 2,
          })}
          <Text fontSize="xs" textColor="tertiary">
            The step to go to if AI detects damaged product as the reason
          </Text>
          {!state.nextDamaged && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              Required
            </Text>
          )}
        </Flex>
        <Divider />

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            If customer expresses interest in an exchange, then (optional)
          </Text>
          {stepSelect({
            value: state.nextExchange,
            valueChange: nextExchangeStepChange,
            handleIndex: 3,
          })}
          <Text fontSize="xs" textColor="tertiary">
            The step to go to if the customer expresses interest in exchanging
            the item (optional - falls back to default if not set), 'Wrong
            Product' and 'Damaged' steps override this if true.
          </Text>
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Custom question (optional)
          </Text>
          <RedoTextareaInputField
            placeholder="Why are you returning this item?"
            readOnly={!canEditReturnsSettings}
            setValue={handleCustomQuestionChange}
            value={state.customQuestion || ""}
          />
          <Text fontSize="xxs" textColor="tertiary">
            The initial question shown to customers. Leave empty to use the
            default question. Make sure to not bias the customer with a leading
            question to ensure data is as accurate as possible.
          </Text>
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Custom explanation subtext (optional)
          </Text>
          <RedoTextareaInputField
            placeholder="Please explain in detail why you are returning this item."
            readOnly={!canEditReturnsSettings}
            setValue={handleCustomExplanationSubtextChange}
            value={state.customExplanationSubtext || ""}
          />
          <Text fontSize="xxs" textColor="tertiary">
            The subtext shown below the main question. Leave empty to use the
            default subtext. Make sure to not bias the customer with a leading
            question to ensure data is as accurate as possible.
          </Text>
        </Flex>
      </Flex>
      <Divider />

      <RedoCollapseSubsection title="Advanced options">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Predefined categories
          </Text>
          <Text fontSize="xxs" textColor="tertiary">
            Optionally define categories for the AI to sort return reasons into.
          </Text>
          <RedoExternalLink
            fontSize="md"
            text="Manage predefined categories"
            url={automationsLink}
          />
        </Flex>
        <Divider />
        <Text fontSize="xs" textColor="secondary">
          Maximum questions
        </Text>
        {getMaxQuestionsValidationErrors(state.maxQuestions).map(
          (error, index) => (
            <Text
              fontSize="xs"
              fontWeight="regular"
              key={index}
              textColor="error"
            >
              {error}
            </Text>
          ),
        )}

        <RedoTextInput
          max={MAX_MAX_QUESTIONS}
          min={MIN_MAX_QUESTIONS}
          placeholder="2"
          setValue={(value: string) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue)) {
              handleMaxQuestionsChange(numValue);
            } else {
              handleMaxQuestionsChange(undefined);
            }
          }}
          state={canEditReturnsSettings ? "default" : "readonly"}
          step={1}
          value={String(state.maxQuestions ?? "")}
        />
        <Text fontSize="xxs" textColor="tertiary">
          {`Maximum number of questions the AI can ask the customer (default: 2, min: ${MIN_MAX_QUESTIONS}, max: ${MAX_MAX_QUESTIONS}). Set to 1 to categorize after a single question with no follow-ups.`}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Guidance
        </Text>
        <RedoTextareaInputField
          readOnly={!canEditReturnsSettings}
          setValue={handleGuidanceChange}
          value={state.merchantGuidance || ""}
        />
        <Text fontSize="xxs" textColor="tertiary">
          Additional instructions for the AI to follow
        </Text>
      </RedoCollapseSubsection>
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          collapsedTitle="Redo user advanced options"
          currentAdditionalAiInfo={state.additionalAiInfo}
          setAdditionalAiInfo={handleAdditionalAiInfo}
        />
      ) : null}
    </>
  );
});
